/**
 * User List Component
 *
 * Displays a comprehensive list of users with:
 * - Search by name or email
 * - Filter by user status (active, suspended, archived) and invitation status
 * - Filter by organization (multi-select)
 * - Filter by role
 * - Pagination with 10, 20, 50, or Show All options
 * - View Details and Edit action buttons
 * - Add User (direct create) and Invite User (send invitation) entry points
 * - Status badges with color coding
 * - Toast notifications for success/error feedback
 */

import * as React from 'react'
import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { useApiQuery, useApiMutation } from '@/api/ApiQueryServiceProvider'
import { useToast } from '@/contexts/ToastContext'
import {
  Search,
  Edit,
  Trash2,
  Users,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Send,
} from 'lucide-react'
import {
  UserStatus,
  USER_STATUS_VARIANTS,
  INVITATION_STATUS_VARIANTS,
  type InvitationStatus,
  type Invitation,
  type User,
} from '@/types/users'
import { findUserInvitation, invitationDisplayStatus } from '@/lib/invitations'

// ============================================================================
// Component Props
// ============================================================================

export interface UserListProps {
  /** "Add User" — create an active user now. */
  onAdd?: () => void
  /** "Invite User" — send an invitation; the user is created when they accept. */
  onInvite?: () => void
}

// ============================================================================
// Component Implementation
// ============================================================================

export function UserList({ onAdd, onInvite }: UserListProps) {
  const { toast } = useToast()

  // State management
  const [searchQuery, setSearchQuery] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState<string>('') // Empty = all statuses
  const [invitationFilter, setInvitationFilter] = React.useState<string>('') // Empty = all invitation statuses
  const [organizationFilter, setOrganizationFilter] = React.useState<string>('') // Empty = all orgs
  const [currentPage, setCurrentPage] = React.useState(1)
  const [itemsPerPage, setItemsPerPage] = React.useState(10)

  // Dialog state (only the delete confirmation remains a dialog)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false)
  const [selectedUser, setSelectedUser] = React.useState<User | null>(null)


  // Fetch users from API
  const { data, isLoading, isError, refetch } = useApiQuery('getUsers', {}, {})
  // Invitation status isn't on the user record — match each user to their latest invitation.
  // Only reflects invitations the caller can see (their primary org + the ones they sent).
  const { data: invitationData } = useApiQuery('getInvitationList', {}, {})
  const invitations: Invitation[] = (invitationData as any)?.content?.invitations ?? []

  // Archive/delete mutation
  const archiveUser = useApiMutation('archiveUser', 'DELETE', {
    onSuccess: () => {
      toast({ title: 'Success', description: 'User has been archived successfully.', type: 'success' })
      setIsDeleteDialogOpen(false)
      setSelectedUser(null)
      refetch()
    },
    onError: (error) => {
      toast({ title: 'Error', description: `Failed to archive user: ${error.message}`, type: 'error' })
    },
  })

  // Fetch organizations for filter dropdown
  const { data: organizationsData } = useApiQuery('getOrganizations', {}, {})
  const organizations = organizationsData?.content?.organizations || []

  // Map API response to component state
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userStatusValues = Object.values(UserStatus) as string[]

  const users: User[] = (data?.content?.users || []).filter((user: any) =>
    !user.isArchived && user.userStatus?.toLowerCase() !== UserStatus.Archived
  ).map((user: any) => {
    // Normalize userStatus to lowercase to match UserStatus values
    let normalizedStatus: UserStatus = UserStatus.Active
    if (user.userStatus) {
      const statusLower = String(user.userStatus).toLowerCase()
      if (userStatusValues.includes(statusLower)) {
        normalizedStatus = statusLower as UserStatus
      }
    }

    const invitation = findUserInvitation(invitations, user)
    const normalizedInvitation: InvitationStatus | undefined = invitation ? invitationDisplayStatus(invitation) : undefined

    // Backend now returns full objects, extract IDs for filtering and names for display
    // Prioritize orgNumber to match the filter dropdown values
    const organizationIds = (user.organizations || []).map((org: any) =>
      typeof org === 'string' ? org : (org.orgNumber?.toString() || org.id)
    ).filter(Boolean)

    // Store organization names directly for display
    const organizationNames = (user.organizations || []).map((org: any) =>
      typeof org === 'string' ? org : org.name
    ).filter(Boolean)

    return {
      userNumber: user.userNumber,
      firstName: user.firstName,
      lastName: user.lastName,
      nickName: user.nickName,
      emailAddress: user.emailAddress,
      mobileNumber: user.mobileNumber,
      countryCode: user.countryCode,
      jobTitle: user.jobTitle,
      profilePicture: user.profilePicture || user.profilePictureLocation,
      userStatus: normalizedStatus,
      invitationStatus: normalizedInvitation,
      organizations: organizationIds,
      // Add display names for direct use
      organizationNames,
      createdAt: user.createdAt,
      updatedAt: user.updatedAt,
    } as any
  })

  // Apply filters
  const filteredUsers = users.filter((user) => {
    // Search filter (firstName, lastName, emailAddress)
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      const matchesSearch = 
        user.firstName.toLowerCase().includes(query) ||
        user.lastName.toLowerCase().includes(query) ||
        (user.emailAddress ?? '').toLowerCase().includes(query)
      
      if (!matchesSearch) return false
    }

    // Status filter
    if (statusFilter && user.userStatus !== statusFilter) {
      return false
    }

    // Invitation status filter
    if (invitationFilter && user.invitationStatus !== invitationFilter) {
      return false
    }

    // Organization filter - compare as strings since user.organizations contains string IDs
    if (organizationFilter) {
      const userOrgIds = (user.organizations || []).map(String)
      if (!userOrgIds.includes(organizationFilter)) {
        return false
      }
    }

    return true
  })

  // Pagination
  const showAll = itemsPerPage === -1
  const totalPages = showAll ? 1 : Math.ceil(filteredUsers.length / itemsPerPage)
  const startIndex = showAll ? 0 : (currentPage - 1) * itemsPerPage
  const endIndex = showAll ? filteredUsers.length : startIndex + itemsPerPage
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex)

  // Handlers
  const handleDelete = (user: User) => {
    setSelectedUser(user)
    setIsDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return
    await archiveUser.mutateAsync({ userNumber: selectedUser.userNumber })
  }

  // Reset pagination when filters change
  React.useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, statusFilter, invitationFilter, organizationFilter, itemsPerPage])

  // Get status badge variant
  const renderVariantBadge = (
    variant: { label: string; variant: string } | undefined,
  ) => {
    const displayVariant = variant || { label: 'Unknown', variant: 'gray' }
    return (
      <Badge
        variant={displayVariant.variant as any}
        className={
          displayVariant.variant === 'blue' ? 'bg-trilogy-blue/10 text-trilogy-blue hover:bg-trilogy-blue/10' :
          displayVariant.variant === 'indigo' ? 'bg-trilogy-navy/10 text-trilogy-navy hover:bg-trilogy-navy/10' :
          displayVariant.variant === 'green' ? 'bg-trilogy-blue/10 text-trilogy-blue hover:bg-trilogy-blue/10' :
          displayVariant.variant === 'amber' ? 'bg-amber-100 text-amber-800 hover:bg-amber-100' :
          displayVariant.variant === 'red' ? 'bg-red-100 text-red-800 hover:bg-red-100' :
          'bg-trilogy-gray/10 text-trilogy-gray hover:bg-trilogy-gray/10'
        }
      >
        {displayVariant.label}
      </Badge>
    )
  }

  const getStatusBadge = (status: UserStatus) =>
    renderVariantBadge(USER_STATUS_VARIANTS[status])

  // Get organization names
  const getOrganizationNames = (orgIds: number[] | undefined = []) => {
    if (!orgIds || orgIds.length === 0) return 'None'
    return orgIds
      .map(id => {
        const org = organizations.find((o: any) => o.id === id.toString() || o.orgNumber === id)
        return org?.name || id.toString()
      })
      .join(', ')
  }


  // Loading state
  if (isLoading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  // Error state
  if (isError) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
          <p className="text-destructive">Failed to load users</p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    )
  }

  // Empty state (no users at all)
  if (users.length === 0) {
    return (
      <>
        <Card className="shadow-sm">
          <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl">Users</CardTitle>
                  <CardDescription className="text-base">Manage user accounts and invitations</CardDescription>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
            <UserPlus className="h-16 w-16 text-muted-foreground" />
            <div className="text-center">
              <h3 className="text-lg font-semibold">Add Your First User</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Create a user now, or invite someone to set up their own account
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => onAdd?.()}>
                <UserPlus className="h-4 w-4 mr-2" />
                Add User
              </Button>
              <Button variant="secondary" onClick={() => onInvite?.()}>
                <Send className="h-4 w-4 mr-2" />
                Invite User
              </Button>
            </div>
          </CardContent>
        </Card>
      </>
    )
  }

  return (
    <div className="space-y-4">
      <Card className="shadow-sm pb-0">
        {/* Header with title and Invite button */}
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">Users</CardTitle>
                <CardDescription className="text-base">Manage user accounts and invitations</CardDescription>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button onClick={() => onAdd?.()} className="w-full sm:w-auto">
                <UserPlus className="h-4 w-4 mr-2" />
                Add User
              </Button>
              <Button variant="secondary" onClick={() => onInvite?.()} className="w-full sm:w-auto">
                <Send className="h-4 w-4 mr-2" />
                Invite User
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            {/* Search */}
            <div className="relative flex-1 lg:max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Filters */}
            <div className="flex flex-col gap-3 sm:flex-row">
              {/* Status Filter */}
              <Select
                value={statusFilter || "all"}
                onValueChange={(val) => setStatusFilter(val === "all" ? "" : val)}
              >
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value={UserStatus.Active}>Active</SelectItem>
                  <SelectItem value={UserStatus.Suspended}>Suspended</SelectItem>
                  <SelectItem value={UserStatus.Archived}>Archived</SelectItem>
                </SelectContent>
              </Select>

              {/* Invitation Status Filter */}
              <Select
                value={invitationFilter || "all"}
                onValueChange={(val) => setInvitationFilter(val === "all" ? "" : val)}
              >
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="All Invitations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Invitations</SelectItem>
                  {(Object.keys(INVITATION_STATUS_VARIANTS) as InvitationStatus[]).map((s) => (
                    <SelectItem key={s} value={s}>{INVITATION_STATUS_VARIANTS[s].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Organization Filter */}
              <Select
                value={organizationFilter || "all"}
                onValueChange={(val) => setOrganizationFilter(val === "all" ? "" : val)}
              >
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="All Organizations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Organizations</SelectItem>
                  {organizations.map((org: any) => (
                    <SelectItem key={org.id || org.orgNumber} value={org.orgNumber?.toString() || org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          {/* Table */}
          <Table className="text-base">
            <TableHeader>
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                <TableHead className="font-semibold text-base">Name</TableHead>
                <TableHead className="font-semibold text-base">Email</TableHead>
                <TableHead className="font-semibold text-base text-center">Status</TableHead>
                <TableHead className="font-semibold text-base">Organizations</TableHead>
                <TableHead className="w-[100px] text-right font-semibold text-base">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No users found. Try adjusting your filters.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsers.map((user) => (
                  <TableRow key={user.userNumber} className="hover:bg-muted/50">
                    <TableCell className="font-medium text-base">
                      <Link
                        to="/users/$userNumber"
                        params={{ userNumber: user.userNumber?.toString() ?? '' }}
                        className="hover:underline text-primary"
                      >
                        {user.firstName} {user.lastName}
                      </Link>
                      {user.nickName && (
                        <span className="text-muted-foreground ml-1">({user.nickName})</span>
                      )}
                    </TableCell>
                    <TableCell>{user.emailAddress}</TableCell>
                    <TableCell className="text-center">
                      {getStatusBadge(user.userStatus)}
                    </TableCell>
                    <TableCell className="text-base text-muted-foreground">
                      {(user as any).organizationNames?.length > 0
                        ? (user as any).organizationNames.join(', ')
                        : getOrganizationNames(user.organizations || [])}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button asChild variant="ghost" size="icon" className="h-8 w-8">
                          <Link
                            to="/users/$userNumber/edit"
                            params={{ userNumber: user.userNumber?.toString() ?? '' }}
                          >
                            <Edit className="h-4 w-4" />
                            <span className="sr-only">Edit {user.firstName}</span>
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => handleDelete(user)}
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">Delete {user.firstName}</span>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {filteredUsers.length > 0 && (
            <div className="flex items-center justify-between border-t py-4">
              <div className="flex items-center gap-4">
                <p className="text-base text-muted-foreground">
                  Showing {showAll ? filteredUsers.length : `${startIndex + 1}-${Math.min(endIndex, filteredUsers.length)}`} of {filteredUsers.length} users
                </p>
                
                {/* Items per page selector */}
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={(val) => setItemsPerPage(val === "-1" ? -1 : parseInt(val))}
                >
                  <SelectTrigger className="w-[130px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="20">20 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                    <SelectItem value="-1">Show All</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Pagination controls */}
              {!showAll && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive User</DialogTitle>
            <DialogDescription>
              This will archive the user. They will no longer be able to access the system.
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">User:</p>
              <p className="text-sm font-medium">{selectedUser.firstName} {selectedUser.lastName}</p>
              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => { setIsDeleteDialogOpen(false); setSelectedUser(null) }}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteConfirm}
                  disabled={archiveUser.isPending}
                >
                  {archiveUser.isPending ? 'Archiving...' : 'Archive'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  )
}
